// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import FancyScroll from "@guestsupply/App/Components/Common/FancyScroll";
import { styledScrollbar } from "@guestsupply/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import { TextField, Typography } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    // XNG-CHANGES
    facetWrapper?: InjectableCss;
    searchTextField?: TextFieldPresentationProps;
    limitedFacetsWrapper?: InjectableCss;
    facetNameTypography?: TypographyProps;
    categoryParentsypography?: TypographyProps;
    facetsScrollWrapper?: InjectableCss;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {};

export interface OwnProps {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const {
        pages: {
            productList: { unfilteredApiParameter },
        },
    } = state;
    if (getProductListDataView(state).value) {
        const productDataView = getProductsDataView(state, unfilteredApiParameter);
        const ProductCollection = productDataView.value ? productDataView : undefined;

        return {
            categoryParents: JSON.parse(ProductCollection?.properties.categoryParents || "{}"),
        };
    }
    return {
        categoryParents : {}
    }
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
    categoryParents,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const anySelected = facets.find(f => f.selected) !== undefined;
    const [searchText, setSearchText] = useState("");
    const [limitedFacets, setLimitedFacets] = useState(facets);
    const [togglePanel, seTtogglePanel] = useState(false);
    useEffect(() => {
        setLimitedFacets(facets);
        if (searchText.length) {
            handleSearchTextChange(searchText);
        }
    }, [facets]);

    const handleSearchTextChange = (searchValue: string) => {
        const fliteredlimitedFacets = facets.filter(facet => facet.name.toLocaleLowerCase().includes(searchValue));
        setLimitedFacets(fliteredlimitedFacets);
    };
    // XNG-XHANGES : Adding fancy scroll and Search Within Filter input
    return (
        <ManagedAccordionSection
            title={title}
            {...styles.accordionSection}
            initialExpanded={expandByDefault || anySelected}
            onTogglePanel={() => {
                seTtogglePanel(!togglePanel);
            }}
        >
            {facets.length > 20 && (
                <TextField
                    {...styles.searchTextField}
                    value={searchText}
                    onChange={event => {
                        setSearchText(event.currentTarget.value);
                        handleSearchTextChange(event.currentTarget.value);
                    }}
                    placeholder={translate("Search Within Filter")}
                    type="text"
                    data-test-selector="productListSearchWithinFilter"
                    id="productsKeywordSearchFilter"
                />
            )}
            <FancyScroll extendedStyles={styles.facetsScrollWrapper} refElement={parentBoxRef}>
                <StyledWrapper ref={parentBoxRef} {...styles.limitedFacetsWrapper}>
                    {limitedFacets?.map(facet => (
                        <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                            <Checkbox
                                {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                                checked={facet.selected}
                                onChange={() => onChangeFacet(facet)}
                                data-test-selector={`facetFilter${facet.id}`}
                            >
                                <StyledWrapper {...styles.facetWrapper}>
                                    <Typography {...styles.facetNameTypography}>{facet.name}</Typography>
                                    {facet.count > -1 && (
                                        <Typography weight={400}>{facet.count !== -1 && `(${facet.count})`}</Typography>
                                    )}
                                </StyledWrapper>
                                {categoryParents[`${facet.id}`] && (
                                    <StyledWrapper>
                                        <Typography {...styles.categoryParentsypography}>
                                            {translate("In:")} {categoryParents[facet.id]}
                                        </Typography>
                                    </StyledWrapper>
                                )}
                            </Checkbox>
                        </CheckboxGroup>
                    ))}
                </StyledWrapper>
            </FancyScroll>
        </ManagedAccordionSection>
    );
};

export default connect(mapStateToProps)(ProductListFiltersAccordionSection);
